/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form, Row, Col, Checkbox, Space, Spin, Modal, message,
} from 'antd';
import { Trans, useTranslation, I18nextProvider } from 'react-i18next';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
  FormItem, Consent,
  FormInput, FormInputPassword, InviteHr,
  ConfidentialityClausule,
} from '../Forms';
import CustomTrans from '../CustomTrans';
import CustomCaretButton from './CustomCaretButton';
import ConfirmAndLoginButton from './ConfirmAndLoginButton';
import GoogleLoginButton from './GoogleLoginButton';
import { CustomCheckBox } from '../Containers';
import Path from '../../utils/Path';
import {
  ConsentService, TokenService, UserService,
} from '../../services';
import env from '../../config/env';
import { RedirectType } from '../../pages/Invite/redirectType.enum';
import CompanyConsents from './CompanyConsents';
import localStorageFacade, { LocalStorageKeys } from '../../utils/localStorageFacade';
import translations from '../../config/translations';

const INVITATION_CONSENTS_SET_FOR_LOGIN = [
  'CONSENT.CONSENT_MONITORING_SERVICE',
];
const INVITATION_CONSENTS_PERNAMENT_SET_FOR_LOGIN = [
  'CONSENT.CONSENT_MONITORING_SERVICE_PERMANENT',
];

const LOGIN_VIEW = 'LOGIN_VIEW';
const CONSENT_ALREADY_GIVEN_VIEW = 'CONSENT_ALREADY_GIVEN_VIEW';
const MULTIPLE_COMPANIES_VIEW = 'MULTIPLE_COMPANIES_VIEW';

const CONSENT_TYPE_PERMANENT_VEHICLE_MONITORING = 'PERMANENT_VEHICLE_MONITORING';
const CONSENT_TYPE_LONG_TIME = 'LONG_TIME';

const Login = ({
  regionFormConfig, companyId, invitingCompany, redirectType,
}) => {
  const { t } = useTranslation();
  const [validLoginForm, setValidLoginForm] = useState(false);
  const location = useLocation();

  const [consents, setConsents] = useState([]);
  const [spin, setSpin] = useState(true);
  const [clickLoginFailed, setClickLoginFailed] = useState(false);
  const [loginForm] = Form.useForm();
  const [invalidUsernameOrPassword, setInvalidUsernameOrPassword] = useState(false);
  const successScreenUrl = `${env.api.track}?successScreen=invitation`;
  const [view, setView] = useState(LOGIN_VIEW);
  const [companiesConsents, setCompaniesConsents] = useState([]);
  const consentType = location.pathname === Path.permInvite()
    ? CONSENT_TYPE_PERMANENT_VEHICLE_MONITORING
    : CONSENT_TYPE_LONG_TIME;

  const loadConsents = () => {
    if (location.pathname === Path.permInvite()) {
      setConsents(INVITATION_CONSENTS_PERNAMENT_SET_FOR_LOGIN);
    } else {
      setConsents(INVITATION_CONSENTS_SET_FOR_LOGIN);
    }
  };

  const onChangeConsents = () => {
    const allCheckedConsents = consents.map((e) => loginForm.getFieldValue([[e]])).every((e) => e === true);
    if (allCheckedConsents) {
      loginForm.setFieldsValue({
        consent: true,
      });
    } else {
      loginForm.setFieldsValue({
        consent: false,
      });
    }
  };

  const warningConsentToSameCompany = () => {
    Modal.warning({
      title: t('FORM_USER_ACCOUNT.THIS_ACTION_IS_FORBIDDEN'),
      content: (
        <>
          <I18nextProvider i18n={translations.registration}>
            <Trans
              i18nKey="FORM_USER_ACCOUNT.CANNOT_GIVE_CONSENT_TO_OWN_COMPANY"
            />
            <br />
            <Trans
              i18nKey="FORM_USER_ACCOUNT.PLEASE_CONTACT_ONBOARDING_TEAM"
            >
              <a href={`mailto:${t('COMMON.SUPPORT')}`}>{t('COMMON.SUPPORT')}</a>
            </Trans>
          </I18nextProvider>
        </>
      ),
      onOk() { setSpin(false); },
      width: '482px',
    });
  };

  const resolveConsents = (companyConsents, givingCompanies) => {
    setCompaniesConsents(companyConsents);
    switch (companyConsents.length) {
      case 0:
        // show error
        break;
      case 1:
        if (companyConsents[0].exists) {
          setView(CONSENT_ALREADY_GIVEN_VIEW);
          setSpin(false);
        } else {
          const givingCompanyId = givingCompanies[0].id;
          if (companyId === givingCompanyId) {
            warningConsentToSameCompany();
            break;
          }
          const command = {
            type: consentType,
            givingCompaniesIds: [givingCompanyId],
            recipientCompanyId: companyId,
          };
          ConsentService.createConsents(command)
            .then(() => {
              window.location.href = successScreenUrl;
            })
            .catch((error) => {
              const payload = error.response.data;
              switch (payload?.errorCode) {
                case 'CONSENT_BETWEEN_SAME_COMPANY_NOT_ALLOWED':
                  setView(CONSENT_ALREADY_GIVEN_VIEW);
                  break;
                default:
                  message.error(t('GENERIC_ERROR'));
              }
              setSpin(false);
            });
        }
        break;
      default:
        setView(MULTIPLE_COMPANIES_VIEW);
        setSpin(false);
    }
  };

  const getConsents = (user) => {
    const { data: { relatedCompanies } } = user;
    const givingCompanies = relatedCompanies.map(({ id, name }) => ({ id, name }));
    const query = {
      type: consentType,
      givingCompanies,
      recipientCompanyId: companyId,
    };
    ConsentService.getConsents(query)
      .then((companyConsents) => {
        resolveConsents(companyConsents.data, givingCompanies);
      });
  };

  const fetchUser = () => {
    UserService.fetchLoggedUser()
      .then((user) => {
        getConsents(user);
      });
  };

  const onFinishLoginForm = (values) => {
    setSpin(true);
    TokenService.generate(values.username, values.password)
      .then((token) => {
        /* eslint-disable camelcase */
        const { data: { access_token } } = token;
        localStorageFacade.put(LocalStorageKeys.AUTH_TOKEN, `${access_token}`);
        TokenService.exchangeSso()
          .then(() => {
            fetchUser();
          })
          .catch(() => {
            setSpin(false);
          });
      })
      .catch((error) => {
        setSpin(false);
        // eslint-disable-next-line
        if (error.response.status === 401) {
          setInvalidUsernameOrPassword(true);
        }
      });
  };

  const onValidLoginForm = () => {
    if (loginForm.isFieldsTouched(consents, true)) {
      loginForm.validateFields()
        .then(() => {
          setValidLoginForm(true);
          setClickLoginFailed(false);
        }).catch(() => {
          setValidLoginForm(false);
        });
    }
  };

  const onGoogleLogin = () => {
    const allCheckedConsents = consents.map((e) => loginForm.getFieldValue([[e]])).every((e) => e === true);
    if (allCheckedConsents) {
      setSpin(true);
      UserService.removeSsoIfPresent(`${window.location.href}&redirect=${RedirectType.LOGOUT}`);
    } else {
      setClickLoginFailed(true);
    }
  };

  useEffect(() => {
    loadConsents();
    switch (redirectType) {
      case RedirectType.GOOGLE:
        UserService.authenticateBySso()
          .then((response) => {
            localStorageFacade.put(LocalStorageKeys.AUTH_TOKEN, `${response.accessToken}`);
            fetchUser();
          });
        break;
      default:
        setSpin(false);
        break;
    }
  }, []);

  return (
    <Spin spinning={spin}>
      {view === 'LOGIN_VIEW'
        ? (
          <Form
            size="large"
            requiredMark={false}
            form={loginForm}
            name="control-hooks"
            onFinish={(v) => onFinishLoginForm(v)}
            onChange={() => onValidLoginForm()}
            onFinishFailed={() => setClickLoginFailed(true)}
          >
            <Row style={{
              marginTop: '5px',
              marginBottom: '-9px',
            }}
            >
              <Col span={5} />
              <Col span={14}>
                <FormItem
                  marginBottom="-10px"
                  style={{
                  }}
                  labelCol={{ span: 24 }}
                  label={t('FORM_USER_ACCOUNT.USERNAME_OR_EMAIL')}
                  name="username"
                >
                  <FormInput style={{
                    borderColor: invalidUsernameOrPassword ? 'red' : '#d9d9d9',
                  }}
                  />
                </FormItem>
              </Col>
              <Col span={5} />
            </Row>
            {
              invalidUsernameOrPassword
                ? (
                  <Row style={{
                    marginTop: '-24px',
                  }}
                  >
                    <Col span={5} />
                    <Col
                      span={19}
                      style={{
                        fontSize: '12px',
                      }}
                    >
                      {t('FORM_USER_ACCOUNT.INVALID_USERNAME_OR_EMAIL')}
                    </Col>
                  </Row>
                ) : <></>
            }
            <Row style={{
              marginBottom: '-9px',
            }}
            >
              <Col span={5} />
              <Col span={14}>
                <FormItem
                  marginBottom="-10px"
                  key="password"
                  labelCol={{ span: 24 }}
                  label={t('FORM_USER_ACCOUNT.PASSWORD')}
                  name="password"
                >
                  <FormInputPassword
                    visibilityToggle={false}
                    style={{
                      borderColor: invalidUsernameOrPassword ? 'red' : '#d9d9d9',
                    }}
                  />
                </FormItem>
              </Col>
              <Col span={5} />
            </Row>
            <Row style={{
              marginBottom: '25px',
            }}
            >
              <Col
                span={19}
                align="right"
              >
                <Trans>
                  <a
                    href={t('COMMON.SET_UP_A_NEW_PASSWORD_REDIRECTION', {
                      keycloakUrl: env.keycloak.url,
                    })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('FORM_USER_ACCOUNT.SET_UP_A_NEW_PASSWORD')}
                  </a>
                </Trans>
              </Col>
              <Col span={5} />
            </Row>
            <Row style={{ marginTop: '-30px' }}>
              <Col span={1} />
              <Col
                span={22}
              >
                <InviteHr />
              </Col>
            </Row>
            <Row>
              <Col span={1} />
              <Col span={23}>
                <Checkbox.Group>
                  <Space direction="vertical">
                    {consents.map((label) => (
                      <Row key={label} style={{ marginTop: '-40px' }}>
                        <FormItem
                          style={{
                            width: 'full',
                          }}
                          key={label}
                          name={label}
                          valuePropName="checked"
                          rules={[
                            {
                              required: true,
                              transform: (value) => value || undefined,
                              type: 'boolean',
                              message: () => null,
                            },
                          ]}
                        >
                          <CustomCheckBox
                            onChange={() => onChangeConsents()}
                            value={label}
                            key={label}
                            color={clickLoginFailed
                              && (loginForm.getFieldValue(label) === undefined
                                || loginForm.getFieldValue(label) === false)
                              ? 'red' : undefined}
                          >
                            <Consent key={label}>
                              <CustomTrans
                                key={label}
                                label={label}
                                inviting={invitingCompany}
                                company={invitingCompany}
                              />
                            </Consent>
                          </CustomCheckBox>
                        </FormItem>
                      </Row>
                    ))}
                  </Space>
                </Checkbox.Group>
              </Col>
            </Row>
            <Row>
              <Col span={1} />
              <Col span={23}>
                <Row style={{
                  marginTop: '-30px',
                }}
                >
                  <ConfidentialityClausule>
                    <CustomCaretButton
                      header={t('CONFIDENTIALITY_CLAUSULE.HEADER')}
                      content="CONFIDENTIALITY_CLAUSULE.CONTENT"
                      region={regionFormConfig.code}
                    />
                  </ConfidentialityClausule>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col span={24} />
            </Row>
            <Row
              justify="space-between"
              gutter={24}
              style={{
                marginTop: '25px',
                marginBottom: '15px',
              }}
            >
              <Col span={12}>
                <Form.Item>
                  <ConfirmAndLoginButton disabled={validLoginForm} execute={() => loginForm.submit()} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item>
                  <GoogleLoginButton execute={() => onGoogleLogin()} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )
        : (
          <CompanyConsents
            companyId={companyId}
            successScreenUrl={successScreenUrl}
            companiesConsents={companiesConsents}
            consentType={consentType}
            view={view}
            setView={setView}
            spin={spin}
            setSpin={setSpin}
          />
        )}
    </Spin>
  );
};

export default Login;

Login.propTypes = {
  regionFormConfig: PropTypes.any.isRequired,
  companyId: PropTypes.string.isRequired,
  invitingCompany: PropTypes.string.isRequired,
  redirectType: PropTypes.string.isRequired,
};
